import { OPERATIONS, OPERATION_SYMBOL } from '../data/mathFacts';

export function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

// Builds the pool of candidate numbers for one operand: specific target
// numbers (if given) take priority over the min/max range, then excludes
// are filtered out of whichever pool is active.
function operandPool({ range, includeNumbers, excludeNumbers }) {
  const exclude = new Set(excludeNumbers || []);
  let pool;
  if (includeNumbers && includeNumbers.length) {
    pool = includeNumbers.filter(n => !exclude.has(n));
  } else {
    const { min, max } = range;
    pool = [];
    for (let n = min; n <= max; n++) if (!exclude.has(n)) pool.push(n);
  }
  return pool.length ? pool : [range?.min ?? 0];
}

function buildOne(operation, pool) {
  const pick = () => pool[randInt(0, pool.length - 1)];

  if (operation === OPERATIONS.SUBTRACTION) {
    let a = pick();
    let b = pick();
    if (b > a) [a, b] = [b, a];
    return { a, b, answer: a - b };
  }

  if (operation === OPERATIONS.DIVISION) {
    const divisor = pick();
    const quotient = pick();
    const dividend = divisor * quotient;
    return { a: dividend, b: divisor, answer: quotient };
  }

  // addition / multiplication — operands are independent
  const a = pick();
  const b = pick();
  const answer = operation === OPERATIONS.MULTIPLICATION ? a * b : a + b;
  return { a, b, answer };
}

const problemKey = (operation, a, b) => {
  // addition/multiplication are commutative — treat a+b and b+a as duplicates
  if (operation === OPERATIONS.ADDITION || operation === OPERATIONS.MULTIPLICATION) {
    return `${operation}:${[a, b].sort((x, y) => x - y).join(',')}`;
  }
  return `${operation}:${a},${b}`;
};

// Generates a shuffled, de-duplicated set of math fact problems.
// operations: one or more operation ids to rotate between (e.g. addition + subtraction in the same set).
// range: { min, max } — used when includeNumbers is empty.
// includeNumbers: specific target numbers to draw an operand from (e.g. [3, 7]).
// excludeNumbers: numbers to never use as an operand.
export function generateProblemSet({ operations, range, includeNumbers, excludeNumbers, count = 10 }) {
  const ops = operations && operations.length ? operations : [OPERATIONS.ADDITION];
  const pool = operandPool({ range, includeNumbers, excludeNumbers });
  const seen = new Set();
  const problems = [];
  const maxAttempts = count * 30;

  for (let attempts = 0; problems.length < count && attempts < maxAttempts; attempts++) {
    const operation = ops[problems.length % ops.length];
    const { a, b, answer } = buildOne(operation, pool);
    const key = problemKey(operation, a, b);
    if (seen.has(key) && seen.size < pool.length * pool.length * ops.length) continue;
    seen.add(key);
    problems.push({
      id: `${key}-${problems.length}`,
      a, b, operation, answer,
      prompt: `${a} ${OPERATION_SYMBOL[operation]} ${b}`,
    });
  }

  return shuffle(problems);
}

// Generates plausible wrong answers near the correct one, for multiple-choice mode.
export function generateChoices(problem, choiceCount = 4) {
  const { answer } = problem;
  const choices = new Set([answer]);
  const spread = Math.max(3, Math.round(Math.abs(answer) * 0.2) || 3);
  let guardAttempts = 0;
  while (choices.size < choiceCount && guardAttempts < choiceCount * 20) {
    guardAttempts++;
    const delta = randInt(-spread, spread) || 1;
    const candidate = answer + delta;
    if (candidate >= 0) choices.add(candidate);
  }
  return shuffle([...choices]);
}
